import React, {useState, useEffect, useCallback, useRef} from 'react';
import { hot } from 'react-hot-loader';
import useStarfishAPI from 'hooks/useStarfishAPI';
import Form from './Form';
import Toast from '../common/Toast';
import sanitizeHtml from 'sanitize-html'
import moment from 'moment';

// Endpoint URL
const URL = `../api/announcements`;

const DEFAULT_START_DATE = '1970-01-01';
const DEFAULT_END_DATE = '2500-12-31';
const ANNOUNCEMENT_SIZE_LIMIT = 2000;

/**
 * Tenant Admins can enter system announcements for staff and students. Staff announcements will be displayed on
 * screen(s) that staff use (e.g. the staff dashboard) and student announcements will display on student screens
 * such as the Success Network page
 * @constructor
 */
const App = () => {
  const [retrieveResponse, callRetrieveAPI] = useStarfishAPI({ isNewAPI: true });
  const [saveResponse, callSaveAPI, {resetResponse: resetSaveResponse}] = useStarfishAPI({ isNewAPI: true });
  const [studentAnnouncement, setStudentAnnouncement] = useState('');
  const [staffAnnouncement, setStaffAnnouncement] = useState('');

  const getMessage = useCallback((type) => {
    if (!retrieveResponse.success) return '';
    const data = retrieveResponse.data.filter(announcement => announcement.type === type);
    if (!data.length) return '';
    return data[0].announcements[0].message;
  }, [retrieveResponse]);

  const getStartDate = useCallback( () => {
    if (!retrieveResponse.success) return null;
    const studentData = retrieveResponse.data.filter(announcement => announcement.type === 'student');
    if (!studentData.length) return null;
    const startDate = studentData[0].announcements[0].startDate;
    return startDate === DEFAULT_START_DATE ? null : startDate;
  }, [retrieveResponse]);

  const getEndDate = useCallback( () => {
    if (!retrieveResponse.success) return null;
    const studentData = retrieveResponse.data.filter(announcement => announcement.type === 'student');
    if (!studentData.length) return null;
    const endDate = studentData[0].announcements[0].endDate;
    return endDate === DEFAULT_END_DATE ? null : endDate;
  }, [retrieveResponse]);

  const studentAnnouncementRef = useRef();
  const staffAnnouncementRef = useRef();
  const startDateRef = useRef();
  const endDateRef = useRef();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const showToast = toastMessage !== null;

  const getAnnouncements = useCallback(() => {
    callRetrieveAPI(URL, {
      method: 'GET',
      headers: {
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
        Expires: '-1'
      }
    });
  }, []);

  useEffect(() => {
    getAnnouncements();
  }, [getAnnouncements]);

  useEffect(() => {
    if ( saveResponse.success ) {
      studentAnnouncementRef.current = studentAnnouncement;
      staffAnnouncementRef.current = staffAnnouncement;
      startDateRef.current = startDate;
      endDateRef.current = endDate;
      setToastMessage('System Announcements saved.');
      resetSaveResponse();
    }
  }, [saveResponse.success, studentAnnouncement, staffAnnouncement, startDate, endDate]);

  useEffect(() => {
    if ( saveResponse.error ) {
      setToastMessage('An error occurred while saving announcements. Contact support if this error happens again.');
    }
  }, [saveResponse.error]);

  useEffect(() => {
    if ( retrieveResponse.error ) {
      setToastMessage('An error occurred while retrieving announcements. Contact support if this error happens again.');
    }
  }, [retrieveResponse.error]);

  // The purpose of this effect is to set initial values to state variables as well as to ref variables - ones that
  // hold default values for comparison with state variables and get updated only on successful save
  useEffect(() => {
    if ( retrieveResponse.success ) {
      studentAnnouncementRef.current = getMessage('student');
      setStudentAnnouncement(studentAnnouncementRef.current);
      staffAnnouncementRef.current = getMessage('staff');
      setStaffAnnouncement(staffAnnouncementRef.current);
      startDateRef.current = getStartDate();
      setStartDate(startDateRef.current);
      endDateRef.current = getEndDate();
      setEndDate(endDateRef.current);
    }
  }, [retrieveResponse.success, getMessage, getStartDate, getEndDate]);

  //sanitize html to prevent XSS attacks
  const sanitizeAnnouncement = (announcement) => {
    return sanitizeHtml(announcement, {
      allowedTags:
        sanitizeHtml.defaults.allowedTags.concat(['img']),
      allowedAttributes: {
        '*': ['style'],
        'img': ['src', 'alt', 'width', 'height', 'align'],
        'a': ['href', 'name', 'target'],
      },
    });
  };

  const announcementsValid = () => {
    if (!studentAnnouncement && (startDate || endDate)) {
      setToastMessage('You cannot save an empty student announcement with the start or end date set. ' +
        'Your changes were not saved.');
      return false
    }
    if (studentAnnouncement
      && staffAnnouncement
      && studentAnnouncement.length > ANNOUNCEMENT_SIZE_LIMIT
      && staffAnnouncement.length > ANNOUNCEMENT_SIZE_LIMIT) {
      setToastMessage(`Both announcements are too long and must be shorter than ${ANNOUNCEMENT_SIZE_LIMIT} 
      characters. Your changes were not saved.`);
      return false
    }
    if (studentAnnouncement && studentAnnouncement.length > ANNOUNCEMENT_SIZE_LIMIT) {
      setToastMessage(`Student announcement must be shorter than ${ANNOUNCEMENT_SIZE_LIMIT} characters. 
      Your changes were not saved.`);
      return false
    }
    if (staffAnnouncement && staffAnnouncement.length > ANNOUNCEMENT_SIZE_LIMIT) {
      setToastMessage(`Staff announcement must be shorter than ${ANNOUNCEMENT_SIZE_LIMIT} characters. 
      Your changes were not saved.`);
      return false
    }
    if (endDate !== null && startDate !== null && moment(endDate).isBefore(moment(startDate), 'day')) {
      setToastMessage('Student announcement end date cannot be earlier than the start date. ' +
        'Your changes were not saved.');
      return false
    }
    return true
  };

  const resetAnnouncements = () => {
    setStudentAnnouncement(studentAnnouncementRef.current);
    setStaffAnnouncement(staffAnnouncementRef.current);
    setStartDate(startDateRef.current);
    setEndDate(endDateRef.current);
  };

  const onSave = () => {
    if (!announcementsValid()) return;

    const announcements = {systemAnnouncements: []};

    if (studentAnnouncement !== studentAnnouncementRef.current
      || startDateRef.current !== startDate
      || endDateRef.current !== endDate) {

      let studentData = {message: sanitizeAnnouncement(studentAnnouncement)};
      if (startDate) studentData.startDate = moment(startDate).format('YYYY-MM-DD');
      if (endDate) studentData.endDate = moment(endDate).format('YYYY-MM-DD');

      announcements.systemAnnouncements.push({
        type: 'student',
        announcements: [studentData]
      });
    }

    if (staffAnnouncement !== staffAnnouncementRef.current) {
      announcements.systemAnnouncements.push({
        type: 'staff',
        announcements: [{
          message: sanitizeAnnouncement(staffAnnouncement),
        }]
      });
    }

    if (announcements.systemAnnouncements.length) {
      setToastMessage('Saving announcements.');
      callSaveAPI(`${URL}`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(announcements)
      });
    }
    else {
      setToastMessage('You did not modify any of the announcements.');
    }
  };

  return (
    <>
      <Form startDate={startDate ? moment(startDate) : null}
            setStartDate={setStartDate}
            endDate={endDate ? moment(endDate) : null}
            setEndDate={setEndDate}
            studentAnnouncement={studentAnnouncement}
            setStudentAnnouncement={setStudentAnnouncement}
            staffAnnouncement={staffAnnouncement}
            setStaffAnnouncement={setStaffAnnouncement}
            resetAnnouncements={resetAnnouncements}
            onSave={onSave}/>
      {showToast && <Toast message={toastMessage} onTimeout={() => setToastMessage(null)} />}
    </>
  )
};

export default hot(module)(App);
